import { Actor, CollisionType, Color, Engine, vec, ParticleEmitter, EmitterType, Circle, Sprite } from "excalibur";
import { WEAPONS, VISUALS } from "../config";
import { Virus } from "./Virus";
import { VirusPool } from "../systems/VirusPool";
import { Resources } from "../resources";

/**
 * Cached cytokine sprite for particle emitters (shared across all instances)
 */
let cachedCytokineSprite: Sprite | null = null;

function getCytokineSprite(): Sprite {
    if (!cachedCytokineSprite) {
        cachedCytokineSprite = Resources.Cytokine.toSprite();
        cachedCytokineSprite.scale = vec(0.3, 0.3);
    }
    return cachedCytokineSprite;
}

/**
 * CytokineStorm - AOE Attack
 * 
 * The "Cytokine Storm" is a powerful AOE ability that damages all viruses
 * within a radius. It represents the immune system's extreme response.
 * 
 * Now uses a radial particle effect with cytokine sprites.
 * MEMORY OPTIMIZATION: Ring graphic only updates every 10 pixels to avoid
 * creating hundreds of Circle objects during expansion.
 */
export class CytokineStorm extends Actor {
    private virusPool: VirusPool;
    private maxRadius: number;
    private currentRadius: number = 0;
    private expandSpeed: number = 300; // pixels per second
    private hasDealtDamage: Set<Virus> = new Set();
    private emitter: ParticleEmitter | null = null;
    private lastGraphicRadius: number = 0;
    private ringGraphic: Circle | null = null;

    /**
     * Callback when a virus is killed by the storm. 
     * Allows the parent scene to handle loot drops/scoring.
     */
    public onVirusKill: ((v: Virus) => void) | null = null;

    constructor(x: number, y: number, virusPool: VirusPool, level: number = 1) {
        super({
            name: 'CytokineStorm',
            pos: vec(x, y),
            radius: 1,
            color: Color.fromHex(VISUALS.COLORS.CYTOKINE_STORM),
            collisionType: CollisionType.PreventCollision,
        });
        this.virusPool = virusPool;
        // Scale radius based on level: base + 10 pixels per level above 1
        this.maxRadius = WEAPONS.CYTOKINE_STORM.RADIUS + (level - 1) * 10;
    }

    override onInitialize(engine: Engine): void {
        // Create particle emitter for radial effect
        this.createParticleEmitter();

        // Create initial ring graphic
        this.updateRingGraphic(true);

        // Play cytokine storm sound effect
        Resources.CytokineStorm.play();
    }

    private createParticleEmitter(): void {
        // Use cached sprite for particles
        const cytokineSprite = getCytokineSprite();

        // Create radial particle emitter using correct API
        this.emitter = new ParticleEmitter({
            pos: vec(0, 0),
            emitterType: EmitterType.Circle,
            radius: 10,
            emitRate: 80,
            isEmitting: true,
            particle: {
                minSpeed: 150,
                maxSpeed: 350,
                minAngle: 0,
                maxAngle: Math.PI * 2,
                opacity: 1,
                fade: true,
                life: 800,
                graphic: cytokineSprite,
            },
        });

        this.addChild(this.emitter);
    }

    private updateRingGraphic(force: boolean = false): void {
        // Only update graphic every 10 pixels to reduce object creation
        const radiusStep = 10;
        const quantizedRadius = Math.floor(this.currentRadius / radiusStep) * radiusStep;

        if (!force && quantizedRadius === this.lastGraphicRadius) {
            return; // No change needed
        }

        this.lastGraphicRadius = quantizedRadius;

        // Fade as it expands
        const alpha = 1 - (this.currentRadius / this.maxRadius) * 0.7;
        this.ringGraphic = new Circle({
            radius: Math.max(1, this.currentRadius),
            color: Color.fromRGB(255, 107, 53, alpha * 0.3),
            strokeColor: Color.fromRGB(255, 200, 100, alpha),
            lineWidth: 4,
        });
        this.graphics.use(this.ringGraphic);
    }

    override onPreUpdate(_engine: Engine, delta: number): void {
        // Expand the radius
        this.currentRadius += this.expandSpeed * (delta / 1000);

        // Update emitter radius to match expansion
        if (this.emitter) {
            this.emitter.radius = this.currentRadius * 0.3;

            // Stop emitting near the end
            if (this.currentRadius >= this.maxRadius * 0.8) {
                this.emitter.isEmitting = false;
            }
        }

        // Update ring graphic (throttled to reduce memory allocations)
        this.updateRingGraphic();

        // Check for viruses in range and damage them
        const viruses = this.virusPool.getActiveViruses();
        for (const virus of viruses) {
            if (this.hasDealtDamage.has(virus)) continue;

            const dist = this.pos.distance(virus.pos);
            if (dist <= this.currentRadius) {
                // Mark as damaged
                this.hasDealtDamage.add(virus);

                // Signal to parent scene to kill this virus (handles loot/xp)
                if (this.onVirusKill) {
                    this.onVirusKill(virus);
                } else {
                    // Fallback if no callback
                    virus.kill();
                }
            }
        }

        // Destroy when fully expanded
        if (this.currentRadius >= this.maxRadius) {
            this.kill();
        }
    }

    /**
     * Get the list of viruses that were hit (for XP/antigen spawning)
     */
    public getHitViruses(): Set<Virus> {
        return this.hasDealtDamage;
    }
}
